"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import BlackPill from "@/components/getting-started/BlackPill";
import { CTAButton } from "@/shadcn/ui/button";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { GroqImage } from "@/helpers/Image";

import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

import griBGimg from "@/public/lp2/component1/grid-bg.svg";
import blurBGimg from "@/public/lp2/component3/blur-bg.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function Component3Section({ data }: { data: PageHomeSchema }) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const content1Ref = useRef<HTMLDivElement | any>(undefined);
  const content2Ref = useRef<HTMLDivElement | any>(undefined);
  const cards1Ref = useRef<HTMLDivElement | any>(undefined);
  const cards2Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const component3TL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      component3TL
        .from(content1Ref.current.children, {
          duration: 1,
          stagger: 0.2,
          delay: 0.5,
          scrollTrigger: {
            trigger: content1Ref.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(content2Ref.current.children, {
          y: "2rem",
          stagger: 0.1,
          delay: 0.5,
          scrollTrigger: {
            trigger: content2Ref.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(cards1Ref.current, {
          y: "2rem",
          delay: 0.5,
          clearProps: "all",
          scrollTrigger: {
            trigger: cards1Ref.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(cards2Ref.current, {
          y: "2rem",
          delay: 0.5,
          clearProps: "all",
          scrollTrigger: {
            trigger: cards2Ref.current,
            end: "center center",
            scrub: 1,
          },
        });
    },
    { scope: containerRef }
  );

  return (
    <section
      ref={containerRef}
      className="w-full relative z-[1] overflow-hidden component-3-section-bg-gradient mix-blend-lighten pt-[10rem]"
      id="component-3"
    >
      <div className="container-lp mx-auto relative">
        {/* Backgrounds */}
        <div
          ref={content1Ref}
          className="relative w-full mb-[-16rem] md:mb-[-20rem] lg:mb-[-20rem] xl:mb-[-28rem] 2xl:mb-[-18rem]"
        >
          <HLSVideoPlayer
            controls={false}
            playsInline={true}
            autoPlay={true}
            muted={true}
            loop={true}
            className="w-full h-[70vh] lg:h-auto !max-w-[1920px] mx-auto object-cover pointer-events-none mix-blend-lighten"
            source={{
              embed: false,
              muxVideo: {
                assetId: "",
                filename: "video-bg",
                status: "ready",
                playbackId: "6PVJzBRuf8p65QvPOtrG6K965ehLIT6iOmZx11301hsY",
              },
              embedUrl: "",
              thumbnail: {
                src: "",
                width: 0,
                height: 0,
                lqip: "",
                name: "",
              },
            }}
          />

          <Image
            src={griBGimg}
            alt="Grid background"
            className="absolute z-[-3] left-0 right-0 bottom-0 w-full h-[50vh] lg:h-auto object-cover pointer-events-none"
          />

          <Image
            src={blurBGimg}
            alt="Blur background"
            className="absolute z-[-2] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] pointer-events-none w-max max-w-max lg:w-full lg:max-w-full"
          />
        </div>
        {/* End Backgrounds */}

        <div className="flex flex-col items-center justify-center mx-auto relative z-[1]">
          <div
            ref={content2Ref}
            className="w-full md:w-4/5 lg:w-[65%] flex flex-col items-center justify-center mx-auto mb-[7rem]"
          >
            {data?.component3Texts && (
              <PortableText
                value={data?.component3Texts ?? []}
                components={customRichTextComponent({
                  block: {
                    h6: ({ children }) => (
                      <BlackPill text={children as string} />
                    ),
                    h2: ({ children }) => (
                      <h2 className="gradient-title text-[5rem] lg:text-[7rem] font-[600] tracking-[-0.35rem] lg:tracking-[-0.49rem] lg:leading-[8.875rem] leading-[110%] text-center mt-[4rem] mb-[3rem]">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="text-[2rem] font-[400] leading-[150%] text-white opacity-75 text-center lg:w-[80%] mx-auto">
                        {children}
                      </p>
                    ),
                  },
                  marks: {
                    orangeFlare: ({ children }) => (
                      <span className="blue-shadow">{children}</span>
                    ),
                  },
                })}
              />
            )}
          </div>

          <div
            ref={cards1Ref}
            className="w-full flex flex-col lg:flex-row items-stretch justify-center gap-[2rem] mb-[2rem]"
          >
            {/*  */}
            <div className="component-3-card transition duration-300 ease-in-out hover:scale-[1.02] flex flex-col items-center justify-between pb-[5rem] rounded-[2.5rem] basis-1/3">
              <GroqImage
                src={data?.component3Items?.[0]?.image}
                alt="Asset 1"
                className=""
              />
              <div className="px-[4rem] text-center">
                {data?.component3Items?.[0]?.texts && (
                  <PortableText
                    value={data?.component3Items?.[0]?.texts ?? []}
                    components={customRichTextComponent({
                      block: {
                        h5: ({ children }) => (
                          <h5 className="text-white text-[3rem] font-[700] leading-[110%] mb-[1rem]">
                            {children}
                          </h5>
                        ),
                        normal: ({ children }) => (
                          <p className="text-[#B2B2B2] text-[2.25rem] font-[400] leading-[150%] lg:px-[2rem] mb-0">
                            {children}
                          </p>
                        ),
                      },
                    })}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col items-stretch justify-center gap-[2rem] basis-2/3">
              <div className="flex flex-col lg:flex-row items-stretch justify-center gap-[2rem]">
                <div className="component-3-card transition duration-300 ease-in-out hover:scale-[1.02] flex flex-col items-center justify-between pb-[5rem] rounded-[2.5rem] basis-1/2">
                  <GroqImage
                    src={data?.component3Items?.[1]?.image}
                    alt="Asset 2"
                    className="w-full h-full scale-150"
                  />
                  <div className="px-[4rem] text-center">
                    {data?.component3Items?.[1]?.texts && (
                      <PortableText
                        value={data?.component3Items?.[1]?.texts ?? []}
                        components={customRichTextComponent({
                          block: {
                            h5: ({ children }) => (
                              <h5 className="text-white text-[3rem] font-[700] leading-[110%] mb-[1rem]">
                                {children}
                              </h5>
                            ),
                            normal: ({ children }) => (
                              <p className="text-[#B2B2B2] text-[2.25rem] font-[400] leading-[150%] lg:px-[2rem] mb-0">
                                {children}
                              </p>
                            ),
                          },
                        })}
                      />
                    )}
                  </div>
                </div>

                <div className="component-3-card transition duration-300 ease-in-out hover:scale-[1.02] flex flex-col items-center justify-between rounded-[2.5rem] basis-1/2 relative">
                  <GroqImage
                    src={data?.component3Items?.[2]?.image}
                    alt="Asset 3"
                    className="mx-auto w-full h-full"
                  />
                  <div className="px-[4rem] pb-[4rem] lg:pb-0 text-center lg:absolute lg:bottom-[5rem] lg:left-0 lg:right-0 lg:z-[1]">
                    {data?.component3Items?.[2]?.texts && (
                      <PortableText
                        value={data?.component3Items?.[2]?.texts ?? []}
                        components={customRichTextComponent({
                          block: {
                            h5: ({ children }) => (
                              <h5 className="text-white text-[3rem] font-[700] leading-[110%] mb-[1rem]">
                                {children}
                              </h5>
                            ),
                            normal: ({ children }) => (
                              <p className="text-[#B2B2B2] text-[2.25rem] font-[400] leading-[150%] lg:px-[2rem] mb-0">
                                {children}
                              </p>
                            ),
                          },
                        })}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="component-3-card transition duration-300 ease-in-out hover:scale-[1.02] flex flex-col xl:flex-row items-center xl:items-end justify-between rounded-[2.5rem] gap-[4rem] xl:gap-0">
                <div className="pl-[4rem] pr-[4rem] xl:pr-0 pb-[5rem] order-2 xl:order-1 text-center lg:text-left">
                  {data?.component3Items?.[3]?.texts && (
                    <PortableText
                      value={data?.component3Items?.[3]?.texts ?? []}
                      components={customRichTextComponent({
                        block: {
                          h5: ({ children }) => (
                            <h5 className="text-white text-[3rem] font-[700] leading-[110%] mb-[1rem]">
                              {children}
                            </h5>
                          ),
                          normal: ({ children }) => (
                            <p className="text-[#B2B2B2] text-[2.25rem] font-[400] leading-[150%] lg:px-[2rem] mb-0">
                              {children}
                            </p>
                          ),
                        },
                      })}
                    />
                  )}
                </div>
                <GroqImage
                  src={data?.component3Items?.[3]?.image}
                  alt="Asset 4"
                  className="rounded-[2.5rem] w-full h-full order-1 xl:order-2 xl:max-w-[60%]"
                />
              </div>
            </div>
            {/*  */}
          </div>

          <div
            ref={cards2Ref}
            className="w-full flex flex-col lg:flex-row items-stretch justify-center gap-[2rem] mb-[4rem]"
          >
            <div className="component-3-card transition duration-300 ease-in-out hover:scale-[1.02] flex flex-col lg:flex-row items-center justify-between rounded-[2.5rem] relative basis-1/2">
              <GroqImage
                src={data?.component3Items?.[4]?.image}
                alt="Asset 5"
                className="mx-auto w-full h-full px-[3rem]"
              />
              <div className="px-[4rem] pb-[4rem] lg:pb-0 text-center lg:absolute lg:bottom-[5rem] lg:left-0 lg:right-0 lg:z-[1]">
                {data?.component3Items?.[4]?.texts && (
                  <PortableText
                    value={data?.component3Items?.[4]?.texts ?? []}
                    components={customRichTextComponent({
                      block: {
                        h5: ({ children }) => (
                          <h5 className="text-white text-[3rem] font-[700] leading-[110%] mb-[1rem]">
                            {children}
                          </h5>
                        ),
                        normal: ({ children }) => (
                          <p className="text-[#B2B2B2] text-[2.25rem] font-[400] leading-[150%] lg:px-[2rem] mb-0">
                            {children}
                          </p>
                        ),
                      },
                    })}
                  />
                )}
              </div>
            </div>
            <div className="component-3-card transition duration-300 ease-in-out hover:scale-[1.02] flex flex-col lg:flex-row items-center justify-between rounded-[2.5rem] relative basis-1/2">
              <GroqImage
                src={data?.component3Items?.[5]?.image}
                alt="Asset 6"
                className="mx-auto w-full h-full"
              />
              <div className="px-[4rem] pb-[4rem] lg:pb-0 text-center lg:absolute lg:bottom-[5rem] lg:left-0 lg:right-0 lg:z-[1]">
                {data?.component3Items?.[5]?.texts && (
                  <PortableText
                    value={data?.component3Items?.[5]?.texts ?? []}
                    components={customRichTextComponent({
                      block: {
                        h5: ({ children }) => (
                          <h5 className="text-white text-[3rem] font-[700] leading-[110%] mb-[1rem]">
                            {children}
                          </h5>
                        ),
                        normal: ({ children }) => (
                          <p className="text-[#B2B2B2] text-[2.25rem] font-[400] leading-[150%] lg:px-[2rem] mb-0">
                            {children}
                          </p>
                        ),
                      },
                    })}
                  />
                )}
              </div>
            </div>
          </div>

          {data?.component3CTA && <CTAButton data={data?.component3CTA} />}
        </div>
      </div>
    </section>
  );
}
